import { Paper, Typography } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useState } from "react";
import { convertKeyToLabel } from "../../manuscript/submit/TooltipText";
import TabReviewService, { TabReviewServiceType } from "./TabReviewService";
import {
  Accordion,
  AccordionSummary,
  COLORS_TC,
  StyledHeadTableCell,
  StyledTableRow,
  useStyles,
} from "./TabReviewStyle";
import ArticleComments from "./comments/ArticleComments";

interface TabReviewType {
  editorView: boolean;
  articleId: number;
}

export default function TabReview({ editorView, articleId }: TabReviewType) {
  const { reviewList, isExpanded, setIsExpanded, expandObject, makeReact, postRating }: TabReviewServiceType =
    TabReviewService({ articleId });
  const classes = useStyles();

  let prevRevison = -1;
  const arrayRevision = reviewList.reduce((acc: any, { revision }: any) => {
    if (prevRevison !== revision) {
      acc.push(revision);
      prevRevison = revision;
    }
    return acc;
  }, []);

  const uniqueReviewers = Object.values(
    reviewList.reduce((acc: any, user: any) => {
      acc[user.reviewer.email] = user.reviewer.email;
      return acc;
    }, {})
  );

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6">Reviews for revisions</Typography>
      {arrayRevision.map((revisionNumber: any) => {
        return (
          <OneAccordion
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            expandObject={expandObject}
            makeReact={makeReact}
            postRating={postRating}
            editorView={editorView}
            title={`Revision #${revisionNumber}`}
            data={reviewList.filter(({ revision }: any) => revision === revisionNumber)}
            uniqueReviewers={uniqueReviewers}
          />
        );
      })}
    </Paper>
  );
}

export function OneAccordion({
  title,
  data,
  isExpanded,
  setIsExpanded,
  expandObject,
  makeReact,
  postRating,
  editorView,
  uniqueReviewers,
}: any) {
  const [expanded, setExpanded] = useState(false);

  const LABELS_QUALITY = [
    "significance",
    "originality",
    "design",
    "impact",
    "relevance",
    "abstractQuality",
    "conclusions",
    "referencing",
    "rating",
  ];

  const LABELS_RECOMMENDATION = [
    "Acceptance",
    "ConditionalAcceptance",
    "MinorRevision",
    "MajorRevision",
    "Rejection",
    "Rejection_Inappropriate",
  ];

  const LABELS_NOTE = ["violation", "editorialNeeded", "volunteer"];

  const parseTopLow = (text: string) => {
    if (text?.startsWith("TOP")) {
      const number = parseInt(text.replace("TOP", ""));
      const newText = text.replace("TOP", "Top ") + "%";
      const color = number < 25 ? COLORS_TC.LIGHT_GREEN_1 : number < 50 ? COLORS_TC.LIGHT_GREEN_2 : COLORS_TC.YELLOW;
      return <StyledHeadTableCell style={{ backgroundColor: color }}>{newText}</StyledHeadTableCell>;
    } else if (text?.startsWith("LOW")) {
      const number = parseInt(text.replace("LOW", ""));
      const newText = text.replace("LOW", "Low ") + "%";
      const color = number < 50 ? COLORS_TC.RED : COLORS_TC.ORANGE;
      return <StyledHeadTableCell style={{ backgroundColor: color }}>{newText}</StyledHeadTableCell>;
    }
  };

  const parseValueRow = (label: string) => {
    return data.map(({ review }: any) => {
      if (LABELS_QUALITY.includes(label)) {
        if (!review) return <StyledHeadTableCell style={{ backgroundColor: COLORS_TC.GREY }}>N/A</StyledHeadTableCell>;
        return parseTopLow(review[label]);
      } else if (LABELS_NOTE.includes(label)) {
        if (!review) return <StyledHeadTableCell style={{ backgroundColor: COLORS_TC.GREY }}>N/A</StyledHeadTableCell>;
        return <StyledHeadTableCell>{review[label] ? "Yes" : "No"}</StyledHeadTableCell>;
      } else if (LABELS_RECOMMENDATION.includes(label)) {
        if (!review) return <StyledHeadTableCell style={{ backgroundColor: COLORS_TC.GREY }}>N/A</StyledHeadTableCell>;
        const backgroundColor = review.recommendation === label ? { backgroundColor: COLORS_TC.BLACK } : {};
        return <StyledHeadTableCell style={backgroundColor}></StyledHeadTableCell>;
      } else {
        return <StyledHeadTableCell />;
      }
    });
  };

  return (
    <Accordion square expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary aria-controls="panel1d-content" id="panel1d-header" expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body1">{title}</Typography>
      </AccordionSummary>
      {editorView && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="review table">
            <TableHead>
              <StyledTableRow>
                <StyledHeadTableCell>Quality of Research</StyledHeadTableCell>
                {data.map((obj: any) => {
                  const fullName = `${obj.reviewer?.firstName ?? ""} ${obj.reviewer?.lastName ?? ""}`;
                  return <StyledHeadTableCell>{fullName}</StyledHeadTableCell>;
                })}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {LABELS_QUALITY.map((label: any) => {
                return (
                  <StyledTableRow>
                    <StyledHeadTableCell>{convertKeyToLabel(label)}</StyledHeadTableCell>
                    {parseValueRow(label)}
                  </StyledTableRow>
                );
              })}
            </TableBody>
            <TableHead>
              <StyledTableRow>
                <StyledHeadTableCell colSpan={3}>Recommendation</StyledHeadTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {LABELS_RECOMMENDATION.map((label: any) => {
                return (
                  <StyledTableRow>
                    <StyledHeadTableCell>{convertKeyToLabel(label)}</StyledHeadTableCell>
                    {parseValueRow(label)}
                  </StyledTableRow>
                );
              })}
            </TableBody>
            <TableHead>
              <StyledTableRow>
                <StyledHeadTableCell colSpan={3}>Other Notes</StyledHeadTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {LABELS_NOTE.map((label: any) => {
                return (
                  <StyledTableRow>
                    <StyledHeadTableCell>{convertKeyToLabel(label)}</StyledHeadTableCell>
                    {parseValueRow(label)}
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ArticleComments
        data={data}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        expandObject={expandObject}
        makeReact={makeReact}
        postRating={postRating}
        editorView={editorView}
        uniqueReviewers={uniqueReviewers}
      />
    </Accordion>
  );
}
